import React, { useState, useEffect } from 'react';
import { Button, Input } from 'reactstrap';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import { api, UPLOAD_TIMEOUT_MS } from 'services/api';
import { downloadAuthenticatedFile, fetchAuthenticatedBlobUrl } from 'services/downloadFile';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const useStyles = makeStyles(() => ({
    fileContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '8px',
        padding: '10px 14px',
        backgroundColor: '#f6f9fc',
        borderRadius: '6px',
        border: '1px solid #e9ecef',
    },
    fileHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '6px',
        fontWeight: '600',
        fontSize: '0.9rem',
        color: '#172430',
    },
    fileActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    fileDescription: {
        fontSize: '0.85rem',
        color: '#525f7f',
        marginBottom: '4px',
    },
    fileDate: {
        fontSize: '0.75rem',
        color: '#8898aa',
    },
    actionButtonSave: {
        cursor: 'pointer',
        margin: '10px auto',
        display: 'block',
    },
}));

const formatarData = (dataString) => {
    const data = new Date(dataString);
    return format(data, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
};

const verifyFileIsImage = (fileData) => {
    const imageTypes = ["png", "jpg", "jpeg", "webp"];
    return imageTypes.some((type) => fileData.filename.split(".").includes(type));
};

const TabAnexos = ({ order, reload, endpoint, showToast }) => {
    const classes = useStyles();
    const [fileUploadData, setFileUploadData] = useState({
        process_id: order.id,
        description: null,
        file: null,
    });
    // imagePreview: { url: string, filename: string } | null
    const [imagePreview, setImagePreview] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState(false);

    const handleField = (e) => {
        if (e.target.name === "file") {
            return setFileUploadData((prev) => ({ ...prev, file: e.target.files[0] }));
        }
        return setFileUploadData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const sendFileUploaded = async (e) => {
        e.preventDefault();
        setLoadingFiles(true);
        const formData = new FormData();
        formData.append('description', fileUploadData.description);
        formData.append('file', fileUploadData.file);
        formData.append('process_id', fileUploadData.process_id);
        formData.append('user_id', localStorage.getItem("AUTHOR_ID"));
        try {
            await api.post('process/files/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: UPLOAD_TIMEOUT_MS,
            });
            await reload(endpoint, { loader: false });
        } catch (error) {
            console.error(error.message);
            showToast('error', 'Falha ao carregar o arquivo.');
        }
        setLoadingFiles(false);
    };

    const handleDeleteFile = async (id) => {
        try {
            await api.delete(`process/files/${id}/`);
            await reload(endpoint, { loader: false });
        } catch (error) {
            console.error(error.message);
            showToast('error', 'Falha ao deletar o arquivo.');
        }
    };

    const handleDownloadFile = async (fileData) => {
        try {
            await downloadAuthenticatedFile(fileData.file, fileData.filename);
        } catch (error) {
            console.error(error);
            showToast('error', 'Falha ao baixar o arquivo.');
        }
    };

    const handleDownloadImage = () => {
        if (!imagePreview) return;
        const link = document.createElement('a');
        link.href = imagePreview.url;
        link.setAttribute('download', imagePreview.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleOpenPreview = async (fileData) => {
        try {
            const url = await fetchAuthenticatedBlobUrl(fileData.file);
            setImagePreview({ url, filename: fileData.filename });
        } catch (error) {
            console.error(error);
            showToast('error', 'Falha ao carregar preview.');
        }
    };

    const closePreview = () => {
        if (imagePreview?.url) {
            window.URL.revokeObjectURL(imagePreview.url);
        }
        setImagePreview(null);
    };

    // Revoga o blob URL ao desmontar ou ao trocar de preview
    useEffect(() => {
        const currentUrl = imagePreview?.url;
        return () => { if (currentUrl) window.URL.revokeObjectURL(currentUrl); };
    }, [imagePreview]);

    const FileComponent = ({ fileData }) => {
        const [confirmDelete, setConfirmDelete] = useState(false);

        return (
            <div className={classes.fileContainer}>
                <div className={classes.fileHeader}>
                    <div>{fileData.filename} ({fileData.size})</div>
                    <div className={classes.fileActions}>
                        {verifyFileIsImage(fileData) ? (
                            <Button className={classes.actionButtonSave} size="sm" color="default" onClick={() => handleOpenPreview(fileData)}>
                                Visualizar
                            </Button>
                        ) : (
                            <Button className={classes.actionButtonSave} size="sm" color="default" onClick={() => handleDownloadFile(fileData)}>
                                Baixar
                            </Button>
                        )}
                        {!confirmDelete ? (
                            <Button className={classes.actionButtonSave} size="sm" color="danger" onClick={() => setConfirmDelete(true)}>
                                Deletar
                            </Button>
                        ) : (
                            <Button size="sm" color="danger" onMouseLeave={() => setConfirmDelete(false)} onClick={() => handleDeleteFile(fileData.id)}>
                                Confirmar
                            </Button>
                        )}
                    </div>
                </div>
                <div className={classes.fileDescription}>
                    {(!fileData.description || fileData.description === "null") ? "Sem descrição" : fileData.description}
                </div>
                <div className={classes.fileDate}>{formatarData(fileData.created_at)}</div>
            </div>
        );
    };

    return (
        <div style={{ height: "100%" }}>
            {imagePreview ? (
                <div style={{ height: "100%", width: "100%" }}>
                    <div id="component-image-preview" style={{ width: "100%", height: "auto" }}>
                        <div className="component-image-button">
                            <Button color='default' size="sm" type='button' onClick={handleDownloadImage}>Download Image</Button>
                            <Button color='danger' size="sm" type='button' onClick={closePreview}>Fechar visualização</Button>
                        </div>
                        <img src={imagePreview.url} alt="Preview do arquivo" />
                    </div>
                </div>
            ) : (
                <div style={{ height: "100%" }}>
                    <div className='file-list-component' style={{ padding: "10px 5px" }}>
                        {order.files?.length === 0 && "Nenhum arquivo para carregar"}
                        {order.files?.map((fileData) => (
                            <FileComponent key={`file-${order.id}-${fileData.id}`} fileData={fileData} />
                        ))}
                    </div>
                    <form onSubmit={sendFileUploaded} style={{ margin: "auto" }}>
                        <div className="custom-file">
                            <input className="custom-file-input" id="customFileLang" name="file" type="file" style={{ display: "none" }} onChange={handleField} />
                            <label className="custom-file-label file-input-button" htmlFor="customFileLang">
                                {fileUploadData.file ? fileUploadData.file.name : "Selecionar Arquivo"}
                            </label>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <Input style={{ boxShadow: "0px 0px 2px black" }} onChange={handleField} type='text' bsSize="md" name="description" placeholder='Digite uma descrição' />
                            {loadingFiles
                                ? <CircularProgress size={24} />
                                : <Button type='submit' size="md" color='primary'>Enviar</Button>}
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default TabAnexos;
